import useServiceDialog from './useServiceDialog';
import { logoutSession } from '../../utils/session';
import { parseJwt } from '../../parseJwt';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  FaArchive,
  FaArrowRight,
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaClipboardList,
  FaFileMedical,
  FaHome,
  FaIndustry,
  FaListUl,
  FaMapMarkerAlt,
  FaPlus,
  FaRegClock,
  FaSave,
  FaSignOutAlt,
  FaTools,
  FaUserCheck,
  FaUserCog
} from 'react-icons/fa';
import api from '../Axios/axios';
import Select from 'react-select';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const STATUS_TEXT = {
  1: 'Yeni',
  2: 'Atandı',
  3: 'Serviste',
  4: 'Personel Tamamladı',
  5: 'Bütçe Onayı',
  6: 'Müdür Onayladı',
  7: 'Muhasebeye Aktarıldı',
  8: 'İptal'
};

const SERVICE_TYPES = [
  { value: '1', label: 'Bakım' },
  { value: '2', label: 'Parça Değişimi' },
  { value: '3', label: 'Arıza Giderme' },
  { value: '4', label: 'Montaj' },
  { value: '5', label: 'Söküm' },
  { value: '6', label: 'Kontrol' },
  { value: '7', label: 'Diğer' }
];

const SERVICE_TYPE_LABELS = SERVICE_TYPES.reduce((map, item) => ({ ...map, [item.value]: item.label }), {
  maintenance: 'Bakım',
  partreplacement: 'Parça Değişimi',
  faultrepair: 'Arıza Giderme',
  assembly: 'Montaj',
  dismantling: 'Söküm',
  inspection: 'Kontrol',
  other: 'Diğer'
});

const normalizeServiceTitle = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text || text === '0') return '';
  const key = text.replace(/[\s_-]/g, '').toLocaleLowerCase('tr-TR');
  if (SERVICE_TYPE_LABELS[text]) return SERVICE_TYPE_LABELS[text];
  if (SERVICE_TYPE_LABELS[key]) return SERVICE_TYPE_LABELS[key];
  const knownType = SERVICE_TYPES.find((item) => item.label.toLocaleLowerCase('tr-TR') === text.toLocaleLowerCase('tr-TR'));
  if (knownType) return knownType.label;
  return text
    .toLocaleLowerCase('tr-TR')
    .replace(/(^|\s|-)(\S)/g, (match) => match.toLocaleUpperCase('tr-TR'));
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const parseToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return {};
  try {
    return parseJwt(token);
  } catch {
    return {};
  }
};

const getUserInfo = () => {
  const payload = parseToken();
  return {
    id: payload.userId || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
    companyId: payload.CompanyId || '',
    name: payload.UserName || payload.name || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || 'Yetkili Servis',
    email: payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || ''
  };
};

const getDisplayNameFromRecord = (record) => {
  if (!record) return '';
  const splitName = `${record.name || record.Name || record.firstName || record.FirstName || ''} ${record.surname || record.Surname || record.lastName || record.LastName || ''}`.trim();
  return splitName || record.fullName || record.FullName || record.userName || record.UserName || '';
};

const isGuid = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const formatDate = (value) =>
  new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));

const formatShortDay = (value) =>
  new Intl.DateTimeFormat('tr-TR', {
    weekday: 'short',
    day: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));

const formatMonthTitle = (value) =>
  new Intl.DateTimeFormat('tr-TR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));

const getDayKey = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const getWeekStart = (value = new Date()) => {
  const now = new Date(value);
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  now.setDate(now.getDate() + diff);
  now.setHours(0, 0, 0, 0);
  return now;
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.Message === 'string') return data.Message;
  if (Array.isArray(data?.messages)) return data.messages.join(', ');
  if (data?.errors && typeof data.errors === 'object') return Object.values(data.errors).flat().join(', ');
  return fallback;
};

const getCompany = (request) => request.requestingCompanyName || request.companyName || request.externalCompanyName || 'Firma belirtilmedi';
const getLocation = (request) => request.workSiteLocation || request.externalWorkSiteName || request.workSiteName || '-';
const getSubject = (request) =>
  normalizeServiceTitle(request.serviceType) ||
  normalizeServiceTitle(request.serviceTypeId) ||
  normalizeServiceTitle(request.serviceSubject) ||
  normalizeServiceTitle(request.serviceTypeName) ||
  normalizeServiceTitle(request.subject) ||
  normalizeServiceTitle(request.requestTitle) ||
  'Teknik Servis';
const getCraneLabel = (crane) => {
  if (!crane) return '';
  const serial = crane.SerialNo || crane.serialNumber || crane.serialNo || crane.towerCraneSerialNo || crane.code || crane.Code || '';
  const brand = crane.Brand || crane.brand || crane.brandName || crane.make || crane.manufacturer || '';
  const model = crane.Model || crane.model || crane.Code || crane.modelName || crane.craneModel || crane.machineModel || '';
  const technicalName = [brand, model].filter(Boolean).join(' ').trim();
  return [serial, technicalName].filter(Boolean).join(' - ');
};
const getCraneOwner = (crane, companyById = new Map()) => {
  if (!crane) return '';
  const direct =
    crane.ownerName ||
    crane.ownerCompanyName ||
    crane.towerCraneOwnerName ||
    crane.companyName ||
    crane.companyTitle ||
    crane.ownerTitle ||
    crane.company?.name ||
    crane.owner?.name ||
    '';
  if (direct) return direct;
  return companyById.get(String(crane.ownerId))?.name || '';
};
const normalizeCraneSearch = (value) => String(value || '')
  .toLocaleLowerCase('tr-TR').replace(/ı/g, 'i')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const craneSelectStyles = {
  container: (base) => ({ ...base, minWidth: 0, fontWeight: 400 }),
  control: (base) => ({ ...base, minHeight: 46, borderRadius: 10 }),
  input: (base) => ({ ...base, margin: 0, padding: 0, minWidth: 0,
    'input': { fontSize: '16px !important', padding: '0 !important', border: '0 !important',
      boxShadow: 'none !important', minHeight: '0 !important', width: '100% !important' } }),
  option: (base) => ({ ...base, minHeight: 48, whiteSpace: 'normal', overflowWrap: 'anywhere' }),
  singleValue: (base) => ({ ...base, maxWidth: '100%' }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, overflow: 'hidden' })
};
const getRequestCrane = (request, craneById = new Map()) => {
  const craneId = request.TowerCraneId || request.towerCraneId || request.craneId || request.machineId;
  return craneById.get(String(craneId)) || null;
};
const getMachine = (request, craneById = new Map()) => {
  const craneLabel = getCraneLabel(getRequestCrane(request, craneById));
  const direct =
    request.machineLabel ||
    request.SerialNo ||
    request.serialNo ||
    request.towerCraneSerialNo ||
    request.towerCraneSerial ||
    request.machineSerialNo ||
    request.machineSerialNumber ||
    request.machineCode ||
    request.towerCraneModel ||
    request.machineModel ||
    request.machineName ||
    request.towerCraneName ||
    request.externalMachineName ||
    request.externalMachineModel;
  if (direct && craneLabel && !String(direct).includes(craneLabel)) {
    const directText = String(direct).trim();
    const craneParts = craneLabel.split(' - ').map((part) => part.trim()).filter(Boolean);
    const extraParts = craneParts.filter((part) => part && !directText.toLowerCase().includes(part.toLowerCase()));
    return [directText, ...extraParts].filter(Boolean).join(' - ');
  }
  if (direct) return direct;
  return craneLabel || '-';
};
const getOwner = (request, craneById = new Map(), companyById = new Map()) => {
  const direct =
    request.towerCraneOwnerName ||
    request.ownerName ||
    request.ownerCompanyName ||
    request.craneOwnerName ||
    request.machineOwnerName;
  return direct || getCraneOwner(getRequestCrane(request, craneById), companyById) || 'Vinç sahibi belirtilmedi';
};
const getPersonnel = (request) => {
  const assignmentNames = (request.assignments || [])
    .map((assignment) => assignment.userName || assignment.assignedUserName || assignment.fullName)
    .filter(Boolean)
    .join(', ');
  const logNames = (request.workLogs || [])
    .map((workLog) => workLog.userName || workLog.createdByName)
    .filter(Boolean)
    .join(', ');
  return assignmentNames || logNames || request.assignedPersonnelName || request.assignedTeam || request.personnelName || 'Atama bekliyor';
};

const getPersonnelDisplayName = (person) =>
  person?.fullName ||
  `${person?.firstName || person?.name || ''} ${person?.lastName || person?.surname || ''}`.trim() ||
  person?.userName ||
  person?.email ||
  'Personel';

const createAssignmentDraft = () => ({ localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, userId: '' });
const createPartRow = () => ({ localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, itemName: '', quantity: 1, unit: 'Adet', unitPrice: '', note: '' });
const PANEL_KEYS = ['dashboard', 'create', 'requests', 'budget', 'calendar', 'notifications', 'archive'];

const getPanelFromHash = () => {
  const rawHash = window.location.hash.replace(/^#/, '');
  if (rawHash.startsWith('/technical-service')) {
    const queryText = rawHash.includes('?') ? rawHash.slice(rawHash.indexOf('?') + 1) : '';
    const panel = new URLSearchParams(queryText).get('panel');
    return PANEL_KEYS.includes(panel) ? panel : 'dashboard';
  }
  const panel = rawHash.replace(/^technical-service-/, '');
  return PANEL_KEYS.includes(panel) ? panel : 'dashboard';
};

export default function TechnicalServiceManagerPortalPage() {
  const user = useMemo(() => getUserInfo(), []);
  const [activePanel, setActivePanel] = useState(() => getPanelFromHash());
  const [requests, setRequests] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [users, setUsers] = useState([]);
  const [profileUser, setProfileUser] = useState(null);
  const [towerCranes, setTowerCranes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [assignmentNotes, setAssignmentNotes] = useState({});
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);
  const [selectedBudgetDetail, setSelectedBudgetDetail] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRef = useRef(null);
  const closeDetail = useCallback(() => setDetailRequest(null), []);
  useServiceDialog(detailRef, Boolean(detailRequest), closeDetail);
  const [archiveFilters, setArchiveFilters] = useState({ personnel: '', status: '', search: '' });
  const [managerNote, setManagerNote] = useState('');
  const [managerParts, setManagerParts] = useState([createPartRow()]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const notificationRef = useRef(null);
  const closeNotificationPanel = useCallback(() => setNotificationPanelOpen(false), []);
  useServiceDialog(notificationRef, notificationPanelOpen, closeNotificationPanel);
  useEffect(() => setNotificationPanelOpen(false), [activePanel]);
  const [readNotificationIds, setReadNotificationIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('serviceManagerReadNotifications') || '[]');
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [form, setForm] = useState({
    categoryType: '1',
    towerCraneId: '',
    externalCompanyName: '',
    externalMachineName: '',
    externalMachineModel: '',
    externalWorkSiteName: '',
    workSiteLocation: '',
    operationDate: '',
    subject: '',
    serviceType: '1',
    problemDescription: ''
  });

  const managerDisplayName = useMemo(() => {
    const candidates = [profileUser, ...personnel, ...users].filter(Boolean);
    const matchedUser = candidates.find((item) => {
      const sameId = user.id && String(item.id || item.userId || '').toLowerCase() === String(user.id).toLowerCase();
      const sameEmail = user.email && String(item.email || item.emailAddress || '').toLowerCase() === String(user.email).toLowerCase();
      return sameId || sameEmail;
    });
    return getDisplayNameFromRecord(matchedUser) || user.name || (user.email ? user.email.split('@')[0] : '') || 'Yetkili Servis Müdürü';
  }, [profileUser, personnel, users, user]);

  const switchPanel = (panel) => {
    setActivePanel(panel);
    window.history.pushState({ serviceManagerPanel: panel }, '', `#/technical-service?panel=${panel}`);
  };

  useEffect(() => {
    const handlePopState = (event) => {
      const panelFromState = event.state?.serviceManagerPanel;
      const panelFromHash = getPanelFromHash();
      setActivePanel(PANEL_KEYS.includes(panelFromState) ? panelFromState : panelFromHash);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [requestRes, personnelRes, userRes, profileRes, craneRes, companyRes, rentalRes] = await Promise.all([
        api.get('/TechnicalService/requests'),
        api.get('/TechnicalService/personnel').catch(() => ({ data: { data: [] } })),
        user.companyId ? api.get(`/User?tenantId=${user.companyId}`).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        api.get('/User/Me').catch(() => ({ data: { data: null } })),
        api.get('/TowerCrane').catch(() => ({ data: { data: [] } })),
        api.get('/Company').catch(() => ({ data: { data: [] } })),
        api.get('/Rental', { params: { serviceStatus: 'Approved', includeLineItems: false } }).catch(() => ({ data: { data: [] } }))
      ]);
      setRequests(requestRes?.data?.data || []);
      setPersonnel(personnelRes?.data?.data || []);
      setUsers(userRes?.data?.data || []);
      setProfileUser(profileRes?.data?.data || null);
      setTowerCranes(craneRes?.data?.data || []);
      setCompanies(companyRes?.data?.data || []);
      setRentals(rentalRes?.data?.data || []);
    } catch {
      setMessage({ type: 'error', text: 'Teknik servis verileri yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const craneById = useMemo(() => {
    const map = new Map();
    towerCranes.forEach((crane) => {
      const id = crane?.id ?? crane?.Id;
      if (id !== undefined && id !== null) map.set(String(id), crane);
    });
    return map;
  }, [towerCranes]);

  const companyById = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => {
      if (company?.id !== undefined && company?.id !== null) map.set(String(company.id), company);
    });
    return map;
  }, [companies]);

  const rows = useMemo(
    () =>
      requests
        .map((request) => ({
          ...request,
          statusCode: normalizeStatus(request.status),
          subjectText: getSubject(request),
          companyText: getCompany(request),
          locationText: getLocation(request),
          machineText: getMachine(request, craneById),
          ownerText: getOwner(request, craneById, companyById),
          personnelText: getPersonnel(request)
        }))
        .sort((a, b) => new Date(b.operationDate || b.createdDate || 0) - new Date(a.operationDate || a.createdDate || 0)),
    [requests, craneById, companyById]
  );

  const activeRentalByCraneId = useMemo(() => {
    const map = new Map();
    rentals
      .filter((rental) => rental?.isActive && (rental?.serviceStatus === 2 || rental?.serviceStatus === 'Approved'))
      .forEach((rental) => map.set(Number(rental.towerCraneId), rental));
    return map;
  }, [rentals]);

  const selectedCrane = towerCranes.find((crane) => String(crane.id) === String(form.towerCraneId)) || null;
  const craneOptions = useMemo(() => towerCranes.map((crane) => {
    const owner = getCraneOwner(crane, companyById);
    const label = getCraneLabel(crane) || 'Vinç bilgisi belirtilmedi';
    return { value: String(crane.id), label: owner ? `${label} · ${owner}` : label,
      craneLabel: label, owner, search: normalizeCraneSearch(`${label} ${owner}`) };
  }), [towerCranes, companyById]);
  const selectedRental = activeRentalByCraneId.get(Number(form.towerCraneId)) || null;

  useEffect(() => {
    if (!selectedRental) return;
    setForm((prev) => ({
      ...prev,
      externalWorkSiteName: prev.externalWorkSiteName || selectedRental.workSite || '',
      workSiteLocation: prev.workSiteLocation || selectedRental.workSite || ''
    }));
  }, [selectedRental]);

  const newRequests = rows.filter((request) => request.statusCode === 1);
  const assignedRequests = rows.filter((request) => request.statusCode === 2 || request.statusCode === 3);
  const budgetRequests = rows.filter((request) => request.statusCode === 4 || request.statusCode === 5);
  const completedRequests = rows.filter((request) => request.statusCode === 7);
  const accountingRequests = rows.filter((request) => request.statusCode === 7);
  const archiveRequests = rows.filter((request) => request.statusCode >= 4 && request.statusCode !== 8);
  const openRequests = rows.filter((request) => request.statusCode >= 1 && request.statusCode <= 5);
  const scheduledRequests = rows.filter((request) => request.operationDate && (request.statusCode === 2 || request.statusCode === 3));
  const unreadNotifications = openRequests.filter((request) => !readNotificationIds.includes(String(request.id)));

  const assignableRequest = newRequests.find((request) => request.id === selectedRequestId) || newRequests[0] || null;
  const budgetRequest = budgetRequests.find((request) => request.id === selectedBudgetId) || budgetRequests[0] || null;
  const selectedDrafts = assignableRequest ? selectedAssignments[assignableRequest.id] || [createAssignmentDraft()] : [];
  const selectedDraftUserIds = selectedDrafts.map((draft) => String(draft.userId || '')).filter(Boolean);

  useEffect(() => {
    if (assignableRequest && !selectedAssignments[assignableRequest.id]) {
      setSelectedAssignments((prev) => ({ ...prev, [assignableRequest.id]: [createAssignmentDraft()] }));
    }
  }, [assignableRequest, selectedAssignments]);

  const stats = [
    { label: 'Toplam Talep', value: rows.length, icon: FaClipboardList, tone: 'blue' },
    { label: 'Açık Talep', value: openRequests.length, icon: FaListUl, tone: 'green' },
    { label: 'Serviste', value: assignedRequests.length, icon: FaTools, tone: 'blue' },
    { label: 'Bütçe Bekleyen', value: budgetRequests.length, icon: FaRegClock, tone: 'orange' },
    { label: 'Tamamlanan', value: completedRequests.length, icon: FaCheckCircle, tone: 'green' },
    { label: 'Muhasebeye Aktarılan', value: accountingRequests.length, icon: FaUserCheck, tone: 'blue' }
  ];

  const menuItems = [
    { key: 'dashboard', label: 'Genel Bakış', icon: FaHome },
    { key: 'create', label: 'Talep Oluştur', icon: FaPlus },
    { key: 'requests', label: 'Açılan Talepler', icon: FaListUl, badge: newRequests.length },
    { key: 'budget', label: 'Bütçe Onayı', icon: FaClipboardCheck, badge: budgetRequests.length },
    { key: 'calendar', label: 'Takvim', icon: FaCalendarAlt },
    { key: 'notifications', label: 'Bildirimler', icon: FaBell, badge: unreadNotifications.length },
    { key: 'archive', label: 'Arşiv', icon: FaArchive, badge: archiveRequests.length }
  ];

  const weekStart = getWeekStart(calendarDate);
  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + index);
    return day;
  });
  const weekRequests = scheduledRequests.filter((request) => weekDays.some((day) => getDayKey(request.operationDate) === getDayKey(day)));
  const selectedDayRequests = scheduledRequests
    .filter((request) => getDayKey(request.operationDate) === getDayKey(calendarDate))
    .sort((a, b) => new Date(a.operationDate || 0) - new Date(b.operationDate || 0));

  const filteredArchiveRequests = archiveRequests.filter((request) => {
    const keyword = archiveFilters.search.trim().toLowerCase();
    const matchesKeyword = !keyword || [
      request.requestNo,
      request.subjectText,
      request.companyText,
      request.locationText,
      request.machineText,
      request.personnelText
    ].some((value) => String(value || '').toLowerCase().includes(keyword));
    const matchesPersonnel = !archiveFilters.personnel || request.personnelText.includes(archiveFilters.personnel);
    const matchesStatus = !archiveFilters.status || String(request.statusCode) === archiveFilters.status;
    return matchesKeyword && matchesPersonnel && matchesStatus;
  });

  const updateAssignment = (requestId, localId, userId) => {
    setSelectedAssignments((prev) => ({
      ...prev,
      [requestId]: (prev[requestId] || []).map((draft) => (draft.localId === localId ? { ...draft, userId } : draft))
    }));
  };

  const addAssignment = (requestId) => {
    setSelectedAssignments((prev) => ({
      ...prev,
      [requestId]: [...(prev[requestId] || []), createAssignmentDraft()]
    }));
  };

  const removeAssignment = (requestId, localId) => {
    setSelectedAssignments((prev) => {
      const current = prev[requestId] || [];
      return {
        ...prev,
        [requestId]: current.length > 1 ? current.filter((draft) => draft.localId !== localId) : current
      };
    });
  };

  const getAvailablePersonnel = (currentUserId) =>
    personnel.filter((item) => {
      const itemId = String(item.id || '');
      return itemId === String(currentUserId || '') || !selectedDraftUserIds.includes(itemId);
    });

  const getFileUrl = (fileName) => {
    if (!fileName) return '';
    if (/^https?:\/\//i.test(fileName)) return fileName;
    const root = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
    const cleanPath = String(fileName).replace(/^\/+/, '');
    if (cleanPath.toLowerCase().startsWith('uploads/')) return `${root}/${cleanPath}`;
    return `${root}/uploads/${cleanPath}`;
  };

  const getFileTypeText = (fileType) => {
    const normalized = Number(fileType);
    if (normalized === 3) return 'Teknik Servis Formu';
    if (normalized === 4) return 'Makine Fotoğrafı';
    return 'Dosya';
  };

  const displayMachine = (value) => {
    const text = String(value || '').trim();
    return text && text !== '-' ? text : 'Makine belirtilmedi';
  };

  const openDetail = async (request) => {
    if (!request) return;
    try {
      setDetailLoading(true);
      const response = await api.get(`/TechnicalService/requests/${request.id}`, { skipCache: true });
      const detail = response?.data?.data || request;
      setDetailRequest({
        ...request,
        ...detail,
        statusCode: normalizeStatus(detail.status ?? request.status),
        subjectText: getSubject(detail),
        companyText: getCompany(detail),
        locationText: getLocation(detail),
        machineText: getMachine(detail, craneById),
        ownerText: getOwner(detail, craneById, companyById),
        personnelText: getPersonnel(detail)
      });
    } catch {
      setDetailRequest(request);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignableRequest) return;
    const assignedUserIds = Array.from(new Set(selectedDrafts.map((draft) => draft.userId).filter(Boolean)));
    if (assignedUserIds.length === 0) {
      setMessage({ type: 'error', text: 'En az bir teknik servis personeli seçin.' });
      return;
    }
    if (assignedUserIds.length !== selectedDrafts.map((draft) => draft.userId).filter(Boolean).length) {
      setMessage({ type: 'error', text: 'Aynı personel bir talebe birden fazla atanamaz.' });
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/TechnicalService/requests/assign', {
        requestId: assignableRequest.id,
        assignedUserIds,
        note: assignmentNotes[assignableRequest.id] || ''
      });
      setMessage({ type: 'success', text: 'Personel ataması yapıldı.' });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: getApiErrorMessage(error, 'Personel ataması yapılamadı.') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRequest = async (event) => {
    event.preventDefault();
    setMessage({ type: '', text: '' });

    const effectiveCompanyId =
      form.categoryType === '1'
        ? selectedRental?.rentingCompanyId || user.companyId || selectedCrane?.ownerId || ''
        : user.companyId || selectedCrane?.ownerId || '';

    if (!effectiveCompanyId) {
      setMessage({ type: 'error', text: 'Talep açan firma bilgisi bulunamadı.' });
      return;
    }
    if (form.categoryType === '1' && !form.towerCraneId) {
      setMessage({ type: 'error', text: 'Kayıtlı vinç seçilmelidir.' });
      return;
    }
    if (!form.workSiteLocation.trim() || !form.operationDate || !form.subject.trim()) {
      setMessage({ type: 'error', text: 'Zorunlu alanlar doldurulmalı.' });
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/TechnicalService/requests', {
        requestingCompanyId: form.categoryType === '1' ? selectedRental?.rentingCompanyId || effectiveCompanyId : effectiveCompanyId,
        categoryType: Number(form.categoryType),
        towerCraneId: form.categoryType === '1' ? Number(form.towerCraneId) : null,
        workSiteId: form.categoryType === '1' ? selectedRental?.workSiteId || null : null,
        externalCompanyName: form.categoryType === '2' ? form.externalCompanyName : null,
        externalMachineName: form.categoryType === '2' ? form.externalMachineName : null,
        externalMachineModel: form.categoryType === '2' ? form.externalMachineModel : null,
        externalSerialNo: null,
        externalWorkSiteName: form.externalWorkSiteName || selectedRental?.workSite || null,
        workSiteLocation: form.workSiteLocation || null,
        operationDate: form.operationDate || null,
        subject: form.subject,
        serviceType: Number(form.serviceType),
        problemDescription: form.problemDescription,
        assignedManagerUserId: isGuid(user.id) ? user.id : null
      });
      setMessage({ type: 'success', text: 'Teknik servis talebi oluşturuldu.' });
      setForm({
        categoryType: '1',
        towerCraneId: '',
        externalCompanyName: '',
        externalMachineName: '',
        externalMachineModel: '',
        externalWorkSiteName: '',
        workSiteLocation: '',
        operationDate: '',
        subject: '',
        serviceType: '1',
        problemDescription: ''
      });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: getApiErrorMessage(error, 'Talep oluşturulamadı.') });
    } finally {
      setSubmitting(false);
    }
  };

  const openBudgetDetail = async (request) => {
    if (!request) return;
    try {
      setSelectedBudgetId(request.id);
      const response = await api.get(`/TechnicalService/requests/${request.id}`);
      const detail = response?.data?.data || request;
      setSelectedBudgetDetail(detail);
      setManagerNote(detail?.managerNote || '');
    } catch {
      setSelectedBudgetDetail(request);
    }
  };

  useEffect(() => {
    if (activePanel === 'budget' && budgetRequest && selectedBudgetId !== budgetRequest.id) {
      openBudgetDetail(budgetRequest);
    }
  }, [activePanel, budgetRequest, selectedBudgetId]);

  const handleApproveBudget = async () => {
    const detail = selectedBudgetDetail || budgetRequest;
    if (!detail) return;
    const parts = managerParts.filter((part) => part.itemName.trim());
    const totalPrice = parts.reduce((sum, part) => sum + Number(part.quantity || 0) * Number(part.unitPrice || 0), 0);
    try {
      setSubmitting(true);
      await api.post('/TechnicalService/requests/approve', {
        requestId: detail.id,
        totalPrice,
        managerNote,
        sendToAccounting: true,
        usedPartPrices: [],
        managerUsedParts: parts.map((part) => ({
          itemName: part.itemName,
          quantity: Number(part.quantity || 0),
          unit: part.unit,
          unitPrice: Number(part.unitPrice || 0),
          note: part.note || ''
        }))
      });
      setMessage({ type: 'success', text: 'Talep onaylandı ve muhasebeye aktarıldı.' });
      setManagerParts([createPartRow()]);
      setManagerNote('');
      setSelectedBudgetDetail(null);
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: getApiErrorMessage(error, 'Bütçe onayı yapılamadı.') });
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await logoutSession();
    window.location.href = '/';
  };

  const markNotificationsRead = (items = openRequests) => {
    const next = Array.from(new Set([...readNotificationIds, ...items.map((item) => String(item.id))]));
    setReadNotificationIds(next);
    localStorage.setItem('serviceManagerReadNotifications', JSON.stringify(next));
    setNotificationPanelOpen(false);
  };

  const renderMessage = () =>
    message.text ? <div className={`manager-toast ${message.type}`}>{message.text}</div> : null;

  const renderNotificationMenu = () => (
    <div className="manager-notification-menu">
      <button type="button" className="manager-bell-button" aria-label="Bildirimler" aria-expanded={notificationPanelOpen} onClick={() => setNotificationPanelOpen((prev) => !prev)}>
        <FaBell />
        {unreadNotifications.length > 0 && <em>{unreadNotifications.length}</em>}
      </button>

    </div>
  );

  const renderNotificationPanel = () => (
    <>
      <div className="manager-notification-backdrop" onClick={closeNotificationPanel} />

        <div className="manager-notification-popover" ref={notificationRef} role="dialog" aria-modal="true" aria-label="Bildirimler" tabIndex={-1}>
          <button type="button" className="manager-notification-close" onClick={closeNotificationPanel}>Kapat</button>
          <div>
            <strong>Bildirimler</strong>
            <button type="button" onClick={() => markNotificationsRead()}>Tümünü Okundu Yap</button>
          </div>
          {unreadNotifications.slice(0, 5).map((request) => (
            <button key={request.id} type="button" onClick={() => { setNotificationPanelOpen(false); openDetail(request); }}>
              <span>{request.subjectText}</span>
              <small>{request.machineText} / {request.locationText}</small>
              <time>{formatDateTime(request.operationDate)}</time>
            </button>
          ))}
          {unreadNotifications.length === 0 && <p>Okunmamış bildirim yok.</p>}
        </div>
    </>
  );

  const renderDashboard = () => (
    <>
      <div className="manager-page-title centered">
        <div>
          <h1>Genel Bakış</h1>
        </div>
        <div className="manager-title-actions">
          {renderNotificationMenu()}
          <time>{formatDate(new Date())}</time>
        </div>
      </div>

      <section className="manager-feature-grid">
        <button type="button" className="manager-feature blue" onClick={() => switchPanel('create')}>
          <FaFileMedical />
          <strong>Talep Oluştur</strong>
          <span>Yeni Teknik Servis Talebi Oluştur</span>
          <em>Talep Oluştur <FaArrowRight /></em>
        </button>
        <button type="button" className="manager-feature green" onClick={() => switchPanel('requests')}>
          <FaListUl />
          <strong>Açılan Talepler</strong>
          <span>Personel Ataması Bekleyen Teknik Servis Taleplerini Görüntüle</span>
          <em>{newRequests.length} Atama Bekleyen <FaArrowRight /></em>
        </button>
        <button type="button" className="manager-feature orange" onClick={() => switchPanel('budget')}>
          <FaClipboardCheck />
          <strong>Bütçe Onayı</strong>
          <span>Personelin Tamamladığı İşleri Muhasebeye Aktar</span>
          <em>{budgetRequests.length} Bekleyen <FaArrowRight /></em>
        </button>
      </section>

      <section className="manager-summary-strip">
        {stats.map((item) => {
          const Icon = item.icon;
          return (
            <article key={item.label} className={item.tone}>
              <div>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
              <Icon />
            </article>
          );
        })}
      </section>

      <div className="manager-content-grid">
        <section className="manager-card">
          <div className="manager-card-title">
            <h2>Son Talepler</h2>
            <button type="button" onClick={() => switchPanel('requests')}>Tümünü Gör</button>
          </div>
          <div className="manager-table-list">
            {rows.slice(0, 6).map((request) => (
              <article key={request.id} role="button" tabIndex="0" onClick={() => openDetail(request)} onKeyDown={(event) => event.key === 'Enter' && openDetail(request)}>
                <strong>{request.requestNo || request.code || 'TS'}</strong>
                <span>{request.subjectText}</span>
                <small>{request.machineText} / {request.locationText}</small>
                <time>{formatDateTime(request.operationDate)}</time>
                <em>{STATUS_TEXT[request.statusCode]}</em>
              </article>
            ))}
          </div>
        </section>

        <section className="manager-card">
          <div className="manager-card-title"><h2>Talep Dağılımı</h2></div>
          <div className="manager-donut">
            <div><strong>{rows.length}</strong><span>Toplam Talep</span></div>
          </div>
          <div className="manager-legend">
            {stats.slice(1).map((item) => <p key={item.label}><span />{item.label}<strong>{item.value}</strong></p>)}
          </div>
          <button type="button" className="manager-side-link" onClick={() => switchPanel('archive')}>Arşivi Aç <FaArrowRight /></button>
        </section>
      </div>
    </>
  );

  const renderCreate = () => (
    <section className="manager-form-page">
      <div className="manager-page-title centered">
        <div>
          <h1>Talep Oluştur</h1>
        </div>
      </div>
      <form className="manager-request-form" onSubmit={handleCreateRequest}>
        <div className="manager-form-main">
          <section className="manager-form-section">
            <div className="manager-section-head"><FaFileMedical /><div><h2>1. Talep Bilgileri</h2><p>Talebin temel bilgilerini girin.</p></div></div>
            <div className="manager-field-grid">
              <label>Talep Tipi<select name="categoryType" value={form.categoryType} onChange={(e) => setForm((p) => ({ ...p, categoryType: e.target.value }))}><option value="1">Kayıtlı Vinç</option><option value="2">Dış Teknik Servis</option></select></label>
              <label>Şantiye<input name="externalWorkSiteName" value={form.externalWorkSiteName} onChange={(e) => setForm((p) => ({ ...p, externalWorkSiteName: e.target.value }))} placeholder="Şantiye adını yazın veya seçin" /></label>
              {form.categoryType === '1' ? (
                <div className="wide" style={{ minWidth: 0 }}>
                  <label htmlFor="service-crane-search">Kayıtlı Vinçler</label>
                  <Select inputId="service-crane-search" instanceId="service-crane-search"
                    name="towerCraneId" classNamePrefix="service-crane-picker"
                    options={craneOptions} value={craneOptions.find((item) => item.value === String(form.towerCraneId)) || null}
                    onChange={(item) => setForm((p) => ({ ...p, towerCraneId: item?.value || '' }))}
                    isSearchable isClearable isLoading={loading} styles={craneSelectStyles}
                    placeholder="Vinç ara veya seç…" aria-describedby="service-crane-search-help"
                    filterOption={({ data }, query) => normalizeCraneSearch(query).split(/\s+/).every((word) => data.search.includes(word))}
                    formatOptionLabel={(item, { context }) => context === 'value' ? item.craneLabel : (
                      <div><div>{item.craneLabel}</div>{item.owner && <small>Vinç sahibi: {item.owner}</small>}</div>
                    )}
                    noOptionsMessage={() => 'Aramanıza uygun vinç bulunamadı.'}
                    loadingMessage={() => 'Vinçler yükleniyor…'}
                    menuPortalTarget={document.body} menuPosition="fixed" menuPlacement="auto"
                    maxMenuHeight={220} menuShouldScrollIntoView={false}
                  />
                  <small id="service-crane-search-help">Seri numarası, model veya vinç sahibi yazabilirsiniz.</small>
                </div>
              ) : (
                <>
                  <label>Dış Firma<input value={form.externalCompanyName} onChange={(e) => setForm((p) => ({ ...p, externalCompanyName: e.target.value }))} /></label>
                  <label>Makine<input value={form.externalMachineName} onChange={(e) => setForm((p) => ({ ...p, externalMachineName: e.target.value }))} /></label>
                </>
              )}
            </div>
          </section>

          <section className="manager-form-section">
            <div className="manager-section-head green"><FaMapMarkerAlt /><div><h2>2. Operasyon Bilgileri</h2><p>Ekip planlaması için operasyon bilgilerini girin.</p></div></div>
            <div className="manager-field-grid three">
              <label>Konum<input value={form.workSiteLocation} onChange={(e) => setForm((p) => ({ ...p, workSiteLocation: e.target.value }))} placeholder="Örn: Kocaeli / Dilovası" /></label>
              <label>Operasyon Tarihi<input type="datetime-local" value={form.operationDate} onChange={(e) => setForm((p) => ({ ...p, operationDate: e.target.value }))} /></label>
              <label>Servis Konusu<select value={form.serviceType} onChange={(e) => setForm((p) => ({ ...p, serviceType: e.target.value }))}>{SERVICE_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            </div>
          </section>

          <section className="manager-form-section">
            <div className="manager-section-head orange"><FaClipboardList /><div><h2>3. Problem Detayı</h2><p>Karşılaşılan problemi açıklayın.</p></div></div>
            <div className="manager-field-grid">
              <label className="wide">Başlık<input value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} placeholder="Kısa bir başlık girin" /></label>
              <label className="wide">Problem Açıklaması<textarea rows="3" value={form.problemDescription} onChange={(e) => setForm((p) => ({ ...p, problemDescription: e.target.value }))} placeholder="Problemi detaylı olarak açıklayın..." /></label>
            </div>
            <footer className="manager-form-footer inline">
              <button type="button" onClick={() => setForm((p) => ({ ...p, subject: '', problemDescription: '' }))}>Temizle</button>
              <button type="submit" disabled={submitting}><FaSave />{submitting ? 'Kaydediliyor' : 'Talep Oluştur'}</button>
            </footer>
          </section>
        </div>

        <aside className="manager-help-column compact manager-create-aside">
          <section className="manager-support-card">
            <div className="manager-support-head">
              <FaClipboardCheck />
              <div>
                <h3>Talep Kontrolü</h3>
                <p>Kaydetmeden önce temel alanları hızlıca kontrol edin.</p>
              </div>
            </div>
            <div className="manager-check-list">
              <p className={form.categoryType && (form.categoryType === '2' || form.towerCraneId) ? 'complete' : ''}>
                <span>Talep Bilgileri</span>
                <strong>{form.categoryType && (form.categoryType === '2' || form.towerCraneId) ? 'Hazır' : 'Eksik'}</strong>
              </p>
              <p className={form.workSiteLocation && form.operationDate ? 'complete' : ''}>
                <span>Operasyon</span>
                <strong>{form.workSiteLocation && form.operationDate ? 'Hazır' : 'Eksik'}</strong>
              </p>
              <p className={form.subject ? 'complete' : ''}>
                <span>Problem Detayı</span>
                <strong>{form.subject ? 'Hazır' : 'Eksik'}</strong>
              </p>
            </div>
            <div className="manager-support-note">
              <span>Destek Hattı</span>
              <strong>0546 884 60 01</strong>
            </div>
          </section>
        </aside>
      </form>
    </section>
  );

  const renderRequests = () => (
    <section className="manager-card manager-full manager-workspace-card manager-flow-page">
      <div className="manager-card-title manager-flow-title">
        <div>
          <h2>Açılan Talepler</h2>
          <p>Personel ataması bekleyen talepleri planlayın ve saha ekibine aktarın.</p>
        </div>
      </div>
      <div className="manager-flow-layout">
        <aside className="manager-flow-list">
          <div className="manager-flow-list-head">
            <span>Atama Kuyruğu</span>
            <strong>{newRequests.length} Talep</strong>
          </div>
          {newRequests.map((request) => (
            <button key={request.id} type="button" className={`manager-flow-ticket ${assignableRequest?.id === request.id ? 'active' : ''}`} onClick={() => setSelectedRequestId(request.id)}>
              <span className="manager-ticket-badge">{request.requestNo || 'TS'}</span>
              <strong>{request.subjectText}</strong>
              <small className="manager-ticket-line"><span>Vinç Sahibi</span><b>{request.ownerText}</b></small>
              <small className="manager-ticket-line"><span>Makine</span><b>{displayMachine(request.machineText)}</b></small>
              <time>{formatDateTime(request.operationDate)}</time>
            </button>
          ))}
          {!loading && newRequests.length === 0 && <p className="manager-empty">Atama bekleyen talep yok.</p>}
        </aside>
        <div className="manager-flow-panel">
          {assignableRequest ? (
            <>
              <div className="manager-flow-hero">
                <div className="manager-flow-main">
                  <div className="manager-flow-title-row">
                    <h2>{assignableRequest.subjectText}</h2>
                    <span>{String(assignableRequest.requestNo || '').replace(/^TS-/, '') || 'Teknik Servis Talebi'}</span>
                  </div>
                  <div className="manager-flow-lines">
                    <p><span>Vinç Sahibi</span><strong>{assignableRequest.ownerText}</strong></p>
                    <p><span>Makine</span><strong>{displayMachine(assignableRequest.machineText)}</strong></p>
                    <p><span>Konum</span><strong>{assignableRequest.locationText}</strong></p>
                  </div>
                </div>
              </div>
              <div className="manager-info-strip">
                <article><span>Operasyon</span><strong>{formatDateTime(assignableRequest.operationDate)}</strong></article>
                <article><span>Durum</span><strong>{STATUS_TEXT[assignableRequest.statusCode]}</strong></article>
                <article><span>Konum</span><strong>{assignableRequest.locationText}</strong></article>
              </div>
              <div className="manager-action-card">
                <div className="manager-action-head">
                  <FaUserCheck />
                  <div>
                    <h3>Personel Planlaması</h3>
                    <p>Bu talep için saha personelini seçip atama notunu iletin.</p>
                  </div>
                </div>
                <div className="manager-field-grid">
                  <div className="manager-team-picker wide">
                    <div className="manager-team-picker-head">
                      <div>
                        <strong>Atanacak Ekip</strong>
                        <span>{selectedDraftUserIds.length || 0} personel seçildi</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => addAssignment(assignableRequest.id)}
                        disabled={selectedDraftUserIds.length >= personnel.length}
                      >
                        <FaPlus /> Personel Ekle
                      </button>
                    </div>
                    <div className="manager-team-list">
                      {selectedDrafts.map((draft, index) => (
                        <div className="manager-team-row" key={draft.localId}>
                          <label>
                            {`Personel ${index + 1}`}
                            <select value={draft.userId} onChange={(e) => updateAssignment(assignableRequest.id, draft.localId, e.target.value)}>
                              <option value="">Personel seçin</option>
                              {getAvailablePersonnel(draft.userId).map((item) => (
                                <option key={item.id} value={item.id}>{getPersonnelDisplayName(item)}</option>
                              ))}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="manager-team-remove"
                            onClick={() => removeAssignment(assignableRequest.id, draft.localId)}
                            disabled={selectedDrafts.length === 1}
                          >
                            Kaldır
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <label className="wide">Atama Notu<textarea rows="3" value={assignmentNotes[assignableRequest.id] || ''} onChange={(e) => setAssignmentNotes((p) => ({ ...p, [assignableRequest.id]: e.target.value }))} placeholder="Ekibe iletilecek kısa notu yazın." /></label>
                </div>
                <button type="button" className="manager-primary manager-wide-action" onClick={handleAssign} disabled={submitting}>Personel Atamasını Tamamla</button>
              </div>
            </>
          ) : <p className="manager-empty">Talep seçimi bekleniyor.</p>}
        </div>
      </div>
    </section>
  );

  const renderBudget = () => {
    const workLogs = selectedBudgetDetail?.workLogs || [];
    const budgetFiles = workLogs.flatMap((log) => (log.files || []).map((file) => ({ ...file, workLogUserName: log.userName })));
    const budgetTotal = managerParts.reduce((sum, part) => {
      const quantity = Number(part.quantity) || 0;
      const unitPrice = Number(part.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
    return (
      <section className="manager-card manager-full manager-workspace-card manager-flow-page">
        <div className="manager-card-title manager-flow-title">
          <div>
            <h2>Bütçe Onayı</h2>
            <p>Personelin tamamladığı işlere kalem girin, fotoğrafları inceleyin ve muhasebeye aktarın.</p>
          </div>
        </div>
        <div className="manager-flow-layout budget">
          <aside className="manager-flow-list">
            <div className="manager-flow-list-head">
              <span>Onay Kuyruğu</span>
              <strong>{budgetRequests.length} Talep</strong>
            </div>
            {budgetRequests.map((request) => (
              <button key={request.id} type="button" className={`manager-flow-ticket ${budgetRequest?.id === request.id ? 'active' : ''}`} onClick={() => openBudgetDetail(request)}>
                <span className="manager-ticket-badge">{request.requestNo || 'TS'}</span>
                <strong>{request.subjectText}</strong>
                <small className="manager-ticket-line"><span>Vinç Sahibi</span><b>{request.ownerText}</b></small>
                <small className="manager-ticket-line"><span>Makine</span><b>{displayMachine(request.machineText)}</b></small>
                <small className="manager-ticket-line"><span>Konum</span><b>{request.locationText}</b></small>
                <time>{formatDateTime(request.operationDate || request.updatedDate)}</time>
              </button>
            ))}
            {!loading && budgetRequests.length === 0 && <p className="manager-empty">Bütçe onayı bekleyen talep yok.</p>}
          </aside>
          <div className="manager-flow-panel">
            {budgetRequest ? (
              <>
                <div className="manager-flow-hero budget">
                  <div className="manager-flow-main">
                    <div className="manager-flow-title-row">
                      <h2>{budgetRequest.subjectText}</h2>
                      <span>{String(budgetRequest.requestNo || '').replace(/^TS-/, '') || 'Teknik Servis Talebi'}</span>
                    </div>
                    <div className="manager-flow-lines">
                      <p><span>Vinç Sahibi</span><strong>{budgetRequest.ownerText}</strong></p>
                      <p><span>Makine</span><strong>{displayMachine(budgetRequest.machineText)}</strong></p>
                      <p><span>Konum</span><strong>{budgetRequest.locationText}</strong></p>
                    </div>
                  </div>
                  <div className="manager-flow-meta">
                    <article><span>Tamamlayan</span><strong>{budgetRequest.personnelText}</strong></article>
                    <article><span>Operasyon</span><strong>{formatDateTime(budgetRequest.operationDate)}</strong></article>
                    <article><span>Bütçe</span><strong>{budgetTotal.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</strong></article>
                  </div>
                </div>
                {workLogs.length > 0 && (
                  <div className="manager-worklog-summary">
                    {workLogs.map((log) => (
                      <article key={log.id || log.processDate}>
                        <strong>Personel İşlem Notu</strong>
                        <span>{formatDateTime(log.processDate)}</span>
                        <p>{log.description || log.processDescription || 'İş açıklaması girilmemiş.'}</p>
                      </article>
                    ))}
                  </div>
                )}
                <div className="manager-inline-files">
                  <div className="manager-inline-files-head">
                    <h3>Fotoğraflar ve Dosyalar</h3>
                    <span>{budgetFiles.length} dosya</span>
                  </div>
                  {budgetFiles.length === 0 ? (
                    <p>Personel tarafından yüklenmiş fotoğraf veya dosya bulunmuyor.</p>
                  ) : (
                    <div className="manager-inline-file-grid">
                      {budgetFiles.map((file) => (
                        <a key={file.id || file.fileName || file.path} href={getFileUrl(file.fileName || file.path || file.url)} target="_blank" rel="noreferrer">
                          {String(file.fileName || file.path || file.url || '').match(/\.(jpg|jpeg|png|webp)$/i) ? (
                            <img src={getFileUrl(file.fileName || file.path || file.url)} alt={getFileTypeText(file.fileType)} />
                          ) : (
                            <FaArchive />
                          )}
                          <strong>{getFileTypeText(file.fileType)}</strong>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="manager-action-card budget">
                  <div className="manager-action-head">
                    <FaClipboardCheck />
                    <div>
                      <h3>Bütçe Kalemleri</h3>
                      <p>Parça, işçilik ve servis bedellerini muhasebeye aktarılacak şekilde girin.</p>
                    </div>
                  </div>
                  <div className="manager-part-list">
                    {managerParts.map((part, index) => (
                      <div key={part.localId} className="manager-part-row">
                        <input placeholder="Kalem" value={part.itemName} onChange={(e) => setManagerParts((p) => p.map((x) => x.localId === part.localId ? { ...x, itemName: e.target.value } : x))} />
                        <input type="number" placeholder="Adet" value={part.quantity} onChange={(e) => setManagerParts((p) => p.map((x) => x.localId === part.localId ? { ...x, quantity: e.target.value } : x))} />
                        <input placeholder="Birim" value={part.unit} onChange={(e) => setManagerParts((p) => p.map((x) => x.localId === part.localId ? { ...x, unit: e.target.value } : x))} />
                        <input type="number" placeholder="Birim fiyat" value={part.unitPrice} onChange={(e) => setManagerParts((p) => p.map((x) => x.localId === part.localId ? { ...x, unitPrice: e.target.value } : x))} />
                        {index === managerParts.length - 1 && <button type="button" className="manager-add-part" onClick={() => setManagerParts((p) => [...p, createPartRow()])}>+</button>}
                      </div>
                    ))}
                  </div>
                  <label className="manager-note">Müdür Notu<textarea rows="3" value={managerNote} onChange={(e) => setManagerNote(e.target.value)} placeholder="Muhasebeye veya arşive düşecek notu yazın." /></label>
                  <button type="button" className="manager-primary manager-wide-action" onClick={handleApproveBudget} disabled={submitting}>Onayla ve Muhasebeye Aktar</button>
                </div>
              </>
            ) : <p className="manager-empty">Bütçe talebi seçimi bekleniyor.</p>}
          </div>
        </div>
      </section>
    );
  };

  const renderCalendar = () => (
    <section className="manager-card manager-full">
      <div className="manager-calendar-header">
        <div>
          <h2>Takvim</h2>
          <p>{formatMonthTitle(calendarDate)} saha ekip planı</p>
        </div>
        <div className="manager-calendar-actions">
          <button type="button" onClick={() => setCalendarDate(new Date())}>Bugün</button>
          <button type="button" aria-label="Önceki hafta" onClick={() => setCalendarDate((prev) => {
            const next = new Date(prev);
            next.setDate(next.getDate() - 7);
            return next;
          })}>‹</button>
          <button type="button" aria-label="Sonraki hafta" onClick={() => setCalendarDate((prev) => {
            const next = new Date(prev);
            next.setDate(next.getDate() + 7);
            return next;
          })}>›</button>
          <strong>{formatDate(weekDays[0]).replace(/^\S+\s/, '')} - {formatDate(weekDays[6]).replace(/^\S+\s/, '')}</strong>
        </div>
      </div>
      <div className="manager-calendar-board">
        {weekDays.map((day) => {
          const dayItems = weekRequests
            .filter((request) => getDayKey(request.operationDate) === getDayKey(day))
            .sort((a, b) => new Date(a.operationDate || 0) - new Date(b.operationDate || 0));
          return (
            <section key={day.toISOString()} className={`manager-calendar-day ${getDayKey(day) === getDayKey(calendarDate) ? 'active' : ''}`}>
              <button type="button" className="manager-calendar-day-head" onClick={() => setCalendarDate(day)}>
                <span>{formatShortDay(day)}</span>
                <strong>{dayItems.length} iş</strong>
              </button>
              <div className="manager-calendar-day-body">
                {dayItems.length === 0 ? (
                  <p>Atanmış iş yok</p>
                ) : dayItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="manager-calendar-event"
                    title={`${item.subjectText} | ${item.personnelText || 'Personel bekliyor'} | ${item.machineText} | ${item.locationText}`}
                    onClick={() => openDetail(item)}
                  >
                    <strong title={item.subjectText}>{item.subjectText}</strong>
                    <small title={item.personnelText || 'Personel bekliyor'}><b>Personel</b><span>{item.personnelText || 'Personel bekliyor'}</span></small>
                    <small title={item.machineText}><b>Makine</b><span>{item.machineText}</span></small>
                    <em title={item.locationText}>{item.locationText}</em>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <div className="manager-day-program">
        <h3>{getDayKey(calendarDate) === getDayKey(new Date()) ? 'Bugünün Programı' : `${formatDate(calendarDate)} Programı`}</h3>
        {selectedDayRequests.length === 0 ? (
          <p>Seçili gün için atanmış saha işi bulunmuyor.</p>
        ) : selectedDayRequests.map((request) => (
          <article key={request.id}>
            <strong>{request.subjectText}</strong>
            <span>{request.personnelText}</span>
            <span>{request.machineText} / {request.locationText}</span>
            <em>{STATUS_TEXT[request.statusCode]}</em>
          </article>
        ))}
      </div>
    </section>
  );

  const renderList = (title, data, showPersonnel = false, withFilters = false) => (
    <section className="manager-card manager-full">
      <div className="manager-card-title centered"><h2>{title}</h2></div>
      {withFilters && (
        <div className="manager-filter-bar">
          <input
            value={archiveFilters.search}
            onChange={(event) => setArchiveFilters((prev) => ({ ...prev, search: event.target.value }))}
            placeholder="Talep no, makine, firma veya şantiye ara"
          />
          <select value={archiveFilters.personnel} onChange={(event) => setArchiveFilters((prev) => ({ ...prev, personnel: event.target.value }))}>
            <option value="">Tüm personeller</option>
            {personnel.map((item) => {
              const name = item.fullName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || 'Personel';
              return <option key={item.id || name} value={name}>{name}</option>;
            })}
          </select>
          <select value={archiveFilters.status} onChange={(event) => setArchiveFilters((prev) => ({ ...prev, status: event.target.value }))}>
            <option value="">Tüm durumlar</option>
            <option value="4">Personel Tamamladı</option>
            <option value="5">Bütçe Onayı</option>
            <option value="6">Müdür Onayladı</option>
            <option value="7">Muhasebeye Aktarıldı</option>
          </select>
        </div>
      )}
      <div className="manager-table-list tall">
        {data.map((request) => (
          <article key={request.id} role="button" tabIndex="0" onClick={() => openDetail(request)} onKeyDown={(event) => event.key === 'Enter' && openDetail(request)}>
            <strong>{request.requestNo || request.code || 'TS'}</strong>
            <span>{request.subjectText}</span>
            <small title={showPersonnel ? request.personnelText : `${displayMachine(request.machineText)} / ${request.companyText}`}>
              {showPersonnel ? `Tamamlayan: ${request.personnelText}` : `${displayMachine(request.machineText)} / ${request.companyText}`}
            </small>
            <time>{formatDateTime(request.operationDate || request.updatedDate || request.createdDate)}</time>
            <em>{STATUS_TEXT[request.statusCode]}</em>
          </article>
        ))}
        {!loading && data.length === 0 && <p className="manager-empty">Kayıt bulunamadı.</p>}
      </div>
    </section>
  );

  const renderNotifications = () => (
    <section className="manager-card manager-full manager-notifications-panel">
      <div className="manager-card-title">
        <div>
          <h2>Bildirimler</h2>
          <p>Atama, servis ve bütçe bekleyen açık işleri buradan takip edin.</p>
        </div>
        <button type="button" onClick={() => markNotificationsRead()}>Tümünü okundu yap</button>
      </div>
      <div className="manager-notification-list-modern">
        {unreadNotifications.concat(openRequests.filter((request) => readNotificationIds.includes(String(request.id)))).slice(0, 18).map((request) => {
          const isRead = readNotificationIds.includes(String(request.id));
          return (
            <article key={request.id} className={isRead ? 'read' : ''}>
              <FaBell />
              <div>
                <strong>{request.subjectText}</strong>
                <p>{request.companyText} / {request.machineText} / {request.locationText}</p>
                <small>Planlanan: {formatDateTime(request.operationDate)} · Durum: {STATUS_TEXT[request.statusCode]}</small>
              </div>
              <time>{formatDateTime(request.createdDate || request.updatedDate || request.operationDate)}</time>
              <button type="button" onClick={() => openDetail(request)}>Detay</button>
            </article>
          );
        })}
        {!loading && openRequests.length === 0 && <p className="manager-empty">Bildirim yok.</p>}
      </div>
    </section>
  );

  const renderDetailModal = () => {
    if (!detailRequest) return null;
    const workLogs = detailRequest.workLogs || [];
    const files = workLogs.flatMap((log) => (log.files || []).map((file) => ({ ...file, workLogUserName: log.userName })));
    const usedParts = workLogs.flatMap((log) => log.usedParts || []);

    return (
      <div className="manager-detail-modal-backdrop" onClick={() => setDetailRequest(null)}>
        <section className="manager-detail-modal" ref={detailRef} role="dialog" aria-modal="true" aria-label="Talep detayı" tabIndex={-1} onClick={(event) => event.stopPropagation()}>
          <header>
            <div>
              <span>{detailRequest.requestNo || 'Teknik Servis Talebi'}</span>
              <h2>{detailRequest.subjectText}</h2>
              <p>{STATUS_TEXT[detailRequest.statusCode]} / {formatDateTime(detailRequest.operationDate)}</p>
            </div>
            <button type="button" onClick={() => setDetailRequest(null)}>Kapat</button>
          </header>

          {detailLoading ? (
            <p className="manager-empty">Talep detayı yükleniyor...</p>
          ) : (
            <>
              <div className="manager-detail-grid">
                <article><span>Makine</span><strong>{detailRequest.machineText}</strong></article>
                <article><span>Vinç Sahibi</span><strong>{detailRequest.ownerText}</strong></article>
                <article><span>Şantiye / Konum</span><strong>{detailRequest.locationText}</strong></article>
                <article><span>Tamamlayan Personel</span><strong>{detailRequest.personnelText}</strong></article>
                <article><span>Müdür Bütçesi</span><strong>{detailRequest.totalPrice ? `${Number(detailRequest.totalPrice).toLocaleString('tr-TR')} TL` : '-'}</strong></article>
                <article><span>Muhasebe Durumu</span><strong>{detailRequest.isSentToAccounting ? 'Muhasebeye aktarıldı' : 'Aktarılmadı'}</strong></article>
              </div>

              <div className="manager-detail-section">
                <h3>Personel İşlem Notları</h3>
                {workLogs.length === 0 ? <p>Personel işlem kaydı bulunmuyor.</p> : workLogs.map((log) => (
                  <article key={log.id || log.processDate} className="manager-log-card">
                    <strong>{log.userName || 'Teknik servis personeli'}</strong>
                    <time>{formatDateTime(log.processDate)}</time>
                    <p>{log.description || 'Açıklama girilmemiş.'}</p>
                  </article>
                ))}
              </div>

              <div className="manager-detail-section">
                <h3>Kullanılan Parça / İşçilik</h3>
                {usedParts.length === 0 ? <p>Parça veya işçilik kalemi bulunmuyor.</p> : usedParts.map((part) => (
                  <article key={part.id || part.itemName} className="manager-part-summary">
                    <strong>{part.itemName}</strong>
                    <span>{part.quantity} {part.unit || 'Adet'}</span>
                    <em>{Number(part.unitPrice || 0).toLocaleString('tr-TR')} TL</em>
                  </article>
                ))}
                {detailRequest.managerNote && <p className="manager-manager-note"><strong>Müdür Notu:</strong> {detailRequest.managerNote}</p>}
              </div>

              <div className="manager-detail-section">
                <h3>Fotoğraflar ve Dosyalar</h3>
                {files.length === 0 ? <p>Yüklenmiş fotoğraf veya dosya bulunmuyor.</p> : (
                  <div className="manager-file-grid">
                    {files.map((file) => (
                      <a key={file.id || file.fileName} href={getFileUrl(file.fileName)} target="_blank" rel="noreferrer">
                        {Number(file.fileType) === 4 ? <img src={getFileUrl(file.fileName)} alt={getFileTypeText(file.fileType)} /> : <FaFileMedical />}
                        <strong>{getFileTypeText(file.fileType)}</strong>
                        <span>{file.workLogUserName || 'Personel'}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    );
  };

  const renderContent = () => {
    if (activePanel === 'create') return renderCreate();
    if (activePanel === 'requests') return renderRequests();
    if (activePanel === 'budget') return renderBudget();
    if (activePanel === 'calendar') return renderCalendar();
    if (activePanel === 'notifications') return renderNotifications();
    if (activePanel === 'archive') return renderList('Teknik Servis Arşivi', filteredArchiveRequests, true, true);
    return renderDashboard();
  };

  return (
    <div className="service-manager-shell">
      <aside className="service-manager-sidebar">
        <div className="service-manager-brand"><FaIndustry /><span>Star Crane</span></div>
        <span className="service-manager-menu-label">Teknik Servis</span>
        <nav>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} type="button" className={activePanel === item.key ? 'active' : ''} onClick={() => switchPanel(item.key)}>
                <Icon /><span>{item.label}</span>{item.badge ? <em>{item.badge}</em> : null}
              </button>
            );
          })}
        </nav>
        <div className="service-manager-user">
          <div className="service-manager-user-avatar"><FaUserCog /></div>
          <section>
            <strong>{managerDisplayName}</strong>
            <span>Yetkili Servis Müdürü</span>
          </section>
        </div>
        <button type="button" className="service-manager-logout" onClick={logout}><FaSignOutAlt /><span>Çıkış Yap</span></button>
      </aside>
      {notificationPanelOpen && renderNotificationPanel()}
      <main className="service-manager-main">
        {renderMessage()}
        {renderContent()}
        {renderDetailModal()}
      </main>
    </div>
  );
}
